import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {catchError, finalize, takeUntil} from 'rxjs/operators';
import {of} from 'rxjs';
import {
  FieldAgroOperation,
  FieldAgroOperationService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  AgrotechnicalMeasure,
  AgrotechnicalMeasureService,
  Crop,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {Loading} from '@components/ui/loading/loading';
import {FilterOperator} from '@app/utils/filter-operator';
import {
  AgroOperation,
} from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-parent/passport-menu-parent';
import {InArrayPipePipe} from '@app/pages/electronic-field-map/pages/fields/passport/menu/pipes/in-array-pipe-pipe';
import {ParseJsonPipe} from '@app/pages/electronic-field-map/pages/fields/passport/menu/pipes/parsed-json-pipe';

@Component({
  selector: 'app-field-details-agro-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DataTableComponent,
    Loading,
    InArrayPipePipe,
    ParseJsonPipe,
  ],
  templateUrl: './field-details-agro-tab.component.html',
  styleUrl: './field-details-agro-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsAgroTabComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly fieldAgroOperationService = inject(FieldAgroOperationService);
  private readonly agrotechnicalMeasureService = inject(AgrotechnicalMeasureService);
  readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('typeTemplate') typeTemplate!: TemplateRef<unknown>;
  @ViewChild('cropTemplate') cropTemplate!: TemplateRef<unknown>;

  @Input({ required: true }) fieldId!: string;
  @Input() crops: Crop[] = [];

  tableColumns: TableColumn[] = [];
  readonly tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50],
    loading: false,
    emptyMessage: 'NO_DATA_FOUND',
  };

  rows: FieldAgroOperation[] = [];
  totalRecords = 0;
  loading = false;
  measureTypes: AgrotechnicalMeasure[] = [];

  ngAfterViewInit(): void {
    this.tableColumns = [
      { field: 'originalDate', header: 'Date', type: 'date' },
      {
        field: '',
        header: 'Crop',
        type: 'custom',
        customTemplate: this.cropTemplate,
      },
      {
        field: 'type',
        header: 'Name2',
        type: 'custom',
        customTemplate: this.typeTemplate,
      },
      { field: 'comment', header: 'Comment' },
    ];
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldId']?.currentValue) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    if (!this.fieldId) {
      this.rows = [];
      this.totalRecords = 0;
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.agrotechnicalMeasureService
      .dictionariesAgrotechnicalMeasureGetByQueryPost({})
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as AgrotechnicalMeasure[])),
      )
      .subscribe(types => {
        this.measureTypes = types;
        this.loadOperations();
      });
  }

  private loadOperations(): void {
    this.fieldAgroOperationService
      .cropMonitoringFieldAgroOperationGetByQueryPost({
        filters: [
          {
            filterField: 'fieldId',
            filterOperator: FilterOperator.Equals,
            filterValue: this.fieldId,
          },
          {
            filterField: 'operationTypeFullName',
            filterOperator: FilterOperator.Equals,
            filterValue: AgroOperation.AgroOperation,
          },
        ],
        take: 500,
        offset: 0,
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as FieldAgroOperation[])),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(rows => {
        this.rows = rows ?? [];
        this.totalRecords = this.rows.length;
        this.cdr.markForCheck();
      });
  }
}
